import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Plus, ChevronDown, ChevronUp } from "lucide-react";
import { useSubmitSetupApproval } from "../../hooks/useSetupApprovals";
import { SETUP_APPROVAL_TYPE } from "../../services/setupApprovalApi";
import { useAccounts } from "@/features/accounting/hooks/useAccounting";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { useAuthStore } from "@/features/auth/stores/authStore";

const initialFormData = {
  code: "",
  name: "",
  description: "",
  isAgroforestry: false,
  revenueAccountId: "",
  // Lifecycle
  sowingToGerminationDays: "",
  sowingToSeedlingDays: "",
  sowingToTransplantReadyDays: "",
  sowingToOvergrownDays: "",
  acclimationDays: "",
  plantingToMaturityDays: "",
  harvestingPeriodDays: "",
  stockPercent: "",
};

type FormData = typeof initialFormData;
type LifecycleFieldKey =
  | "sowingToGerminationDays"
  | "sowingToSeedlingDays"
  | "sowingToTransplantReadyDays"
  | "sowingToOvergrownDays"
  | "acclimationDays"
  | "plantingToMaturityDays"
  | "harvestingPeriodDays";
const parseDay = (val: string) => (val === "" ? undefined : parseInt(val, 10));
const parseDecimal = (val: string) => (val === "" ? undefined : parseFloat(val));

export const CreateCropTypeDialog = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [showLifecycle, setShowLifecycle] = useState(false);

  const submitMutation = useSubmitSetupApproval();
  const accountsQuery = useAccounts();
  const { user } = useAuthStore();
  const notify = useNotification();
  const confirm = useConfirm();

  const canConfigureRevenueAccount = (user?.roles ?? [])
    .map((r) => (r ?? "").trim().toUpperCase())
    .some((r) => r === "ADMIN" || r === "FINANCE MANAGER" || r === "ADMIN FINANCE" || r === "ACCOUNTANT" || r === "SYSTEM ADMIN");

  const incomePostingAccounts = (accountsQuery.data ?? []).filter(
    (a) => a.accountType === 4 && a.isActive && !a.isGroup
  );

  const handleChange = (
    field: keyof FormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    const stockPercent = parseDecimal(formData.stockPercent);
    if (stockPercent !== undefined && (stockPercent < 0 || stockPercent > 100)) {
      setFieldErrors({ stockPercent: ["Must be between 0 and 100"] });
      setShowLifecycle(true);
      return;
    }

    const payload = {
      code: formData.code,
      name: formData.name,
      description: formData.description,
      isAgroforestry: formData.isAgroforestry,
      revenueAccountId: formData.revenueAccountId || undefined,
      sowingToGerminationDays: parseDay(formData.sowingToGerminationDays),
      sowingToSeedlingDays: parseDay(formData.sowingToSeedlingDays),
      sowingToTransplantReadyDays: parseDay(formData.sowingToTransplantReadyDays),
      sowingToOvergrownDays: parseDay(formData.sowingToOvergrownDays),
      acclimationDays: parseDay(formData.acclimationDays),
      plantingToMaturityDays: parseDay(formData.plantingToMaturityDays),
      harvestingPeriodDays: parseDay(formData.harvestingPeriodDays),
      stockPercent,
    };

    confirm({
      title: "Submit crop type for approval?",
      description: `“${payload.name}” will be submitted for approval before it becomes available.`,
      confirmLabel: "Submit",
      onConfirm: () =>
        submitMutation
          .mutateAsync({
            approvalType: SETUP_APPROVAL_TYPE.CropType,
            entityJson: JSON.stringify(payload),
            description: `New Crop Type: ${payload.name}`,
          })
          .then(() => {
            notify.success("Submitted for approval");
            setFormData(initialFormData);
            setOpen(false);
          })
          .catch((error: unknown) => {
            notify.error(getErrorMessage(error));
            throw error;
          }),
    });
  };

  const lifecycleFields: { key: LifecycleFieldKey; label: string }[] = [
    { key: "sowingToGerminationDays", label: "Days to Germination" },
    { key: "sowingToSeedlingDays", label: "Days to Seedling" },
    { key: "sowingToTransplantReadyDays", label: "Days to Transplant-Ready" },
    { key: "sowingToOvergrownDays", label: "Days to Overgrown" },
    { key: "acclimationDays", label: "Acclimation Days" },
    { key: "plantingToMaturityDays", label: "Days to Maturity (field)" },
    { key: "harvestingPeriodDays", label: "Harvest Window (days)" },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          New Crop Type
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Crop Type</DialogTitle>
          <DialogDescription>
            Add a new crop type to the system
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {submitMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {submitMutation.error.message}
            </div>
          )}

          <Field>
            <FieldLabel htmlFor="code">
              Code <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="code"
              placeholder="e.g., SEED, SOIL"
              value={formData.code}
              onChange={(e) => handleChange("code", e.target.value)}
              required
              disabled={submitMutation.isPending}
            />
            {fieldErrors.code && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.code.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="name">
              Name <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="name"
              placeholder="e.g., Seeds, Soil, Fertilizer"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
              disabled={submitMutation.isPending}
            />
            {fieldErrors.name && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.name.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="description">Description</FieldLabel>
            <Textarea
              id="description"
              placeholder="Brief description of this crop type"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              disabled={submitMutation.isPending}
            />
          </Field>

          {canConfigureRevenueAccount && (
            <Field>
              <FieldLabel>Revenue Account</FieldLabel>
              <Select
                value={formData.revenueAccountId}
                onValueChange={(value) => handleChange("revenueAccountId", value === "__NONE__" ? "" : value)}
                disabled={submitMutation.isPending || accountsQuery.isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Use default Sales Revenue fallback" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__NONE__">Use default Sales Revenue fallback</SelectItem>
                  {incomePostingAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.accountCode} - {account.accountName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Optional. If unset, sales for this crop type post to the default Sales Revenue account.
              </p>
            </Field>
          )}

          <Field>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isAgroforestry"
                checked={formData.isAgroforestry}
                onCheckedChange={(checked) =>
                  handleChange("isAgroforestry", checked as boolean)
                }
                disabled={submitMutation.isPending}
              />
              <Label htmlFor="isAgroforestry" className="cursor-pointer">
                Agroforestry
              </Label>
            </div>
          </Field>

          {/* Lifecycle Defaults — collapsible */}
          <div className="border rounded-md">
            <button
              type="button"
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50 transition-colors"
              onClick={() => setShowLifecycle((v) => !v)}
            >
              Lifecycle Defaults
              {showLifecycle ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            {showLifecycle && (
              <div className="px-4 pb-4 space-y-3 border-t">
                <p className="text-xs text-muted-foreground pt-3">
                  All fields optional. Default timeline used when no variety-specific values exist.
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
                  {lifecycleFields.map(({ key, label }) => (
                    <Field key={key}>
                      <FieldLabel htmlFor={key}>{label}</FieldLabel>
                      <Input
                        id={key}
                        type="number"
                        min={1}
                        placeholder="days"
                        value={formData[key]}
                        onChange={(e) => handleChange(key, e.target.value)}
                        disabled={submitMutation.isPending}
                      />
                      {fieldErrors[key] && (
                        <div className="text-red-500 text-xs mt-1">
                          {fieldErrors[key].join(", ")}
                        </div>
                      )}
                    </Field>
                  ))}
                </div>

                <Field>
                  <FieldLabel htmlFor="stockPercent">Stock %</FieldLabel>
                  <Input
                    id="stockPercent"
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    placeholder="70"
                    value={formData.stockPercent}
                    onChange={(e) => handleChange("stockPercent", e.target.value)}
                    disabled={submitMutation.isPending}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Percentage of germinated stock that becomes sellable. Filling Reserve:{" "}
                    {(() => {
                      const v = parseFloat(formData.stockPercent);
                      const pct = Number.isFinite(v) ? v : 70;
                      return (100 - pct).toFixed(1);
                    })()}
                    %. Leave blank to use the system default of 70%.
                  </p>
                  {fieldErrors.stockPercent && (
                    <div className="text-red-500 text-xs mt-1">
                      {fieldErrors.stockPercent.join(", ")}
                    </div>
                  )}
                </Field>
              </div>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={submitMutation.isPending}
          >
            {submitMutation.isPending ? "Submitting..." : "Submit for Approval"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

